import React, {ReactElement, useState, useContext, useEffect} from 'react';
import {Tab, Tabs} from '@customink/pigment-react';
import {StoreSettings, StoreSettingsProps} from 'models/storeSettings';
import {Skeleton, Box, Typography} from '@mui/material';
import {useHistory, useLocation} from 'react-router';
import {
  useGetStoreDetailsQuery,
  useGetStoreSettingsQuery
} from '../../api/storesApi';
import {useFetchUserQuery} from '../../api/userApi';
import {useAppSelector} from '../../hooks';
import {MobileContext} from '../../containers/MobileProvider';
import FormSettings from './FormSettings';
import GroupCommunication from './GroupCommunication';
import ContactDetails from './ContactDetails';
import './index.scss';

export const GROUP_ORDER_ALERT_OPTIONS = {
  updateSettings: {
    message: 'Your settings have been saved.'
  },
  cancel: {
    message: 'Your Group Order has been canceled.'
  },
  reopen: {
    title: 'Your Order Has Been Re-opened',
    message:
      'Your order has been re-opened and will close in 7 days. To adjust the close date, use the calendar below.'
  }
} as const;

const StoreSettings: React.FC<StoreSettingsProps> = ({
  storeSlug
}: StoreSettingsProps): ReactElement => {
  const history = useHistory();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  useGetStoreDetailsQuery(storeSlug);
  useFetchUserQuery();
  const {data: storeSettings, isLoading: isStoreSettingsLoading} =
    useGetStoreSettingsQuery(storeSlug);
  const storeDetails = useAppSelector(state => state.stores.storeDetails);
  const capabilities = storeDetails?.capabilities;
  const mobile = useContext(MobileContext);

  const storeSettingsClasses = `storeSettings${mobile ? ' mobile' : ''}`;

  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (e: React.SyntheticEvent, newValue: number) => {
    urlParams.set('tab', newValue.toString());
    history.replace({search: urlParams.toString()});
    setActiveTab(newValue);
  };

  useEffect(() => {
    const tabIndex = parseInt(urlParams.get('tab') ?? '0', 10);
    setActiveTab(tabIndex);
  }, [location.search]);

  const FormPlaceholder: React.FC = () => {
    const mock = [1, 2];
    return (
      <>
        <div className="placeholder">
          {mock.map(
            (row: number): ReactElement => (
              <div className="placeholderForm" key={row}>
                <Box className="placeholderForm-option">
                  <Typography className="title" component="div" variant="h3">
                    <Skeleton />
                  </Typography>
                  <Typography
                    className="subTitle"
                    component="div"
                    variant="bodyLong2">
                    <Skeleton />
                    <Skeleton />
                  </Typography>
                </Box>
                <Box className="placeholderForm-option">
                  <Skeleton className="inputField" variant="rectangular" />
                  <Skeleton className="inputField" variant="rectangular" />
                </Box>
              </div>
            )
          )}
          <div className="placeholderButton">
            {mock.map(
              (row: number): ReactElement => (
                <Skeleton key={row} className="button" variant="rectangular" />
              )
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <div className={storeSettingsClasses}>
      {!storeSettings || !storeDetails ? (
        <FormPlaceholder />
      ) : (
        <>
          <Typography variant="h6" className="settingsTitle">
            Settings
          </Typography>
          <Tabs
            className="settingsTabs"
            value={activeTab}
            onChange={handleTabChange}>
            <Tab label="Page Settings" value={0} />
            {storeDetails.campaigns?.length > 0 && (
              <Tab label="Contact Details" value={1} />
            )}
            {capabilities?.includes('configureGroupCommunication') ? (
              <Tab label="Group Communication" value={2} />
            ) : null}
          </Tabs>

          {activeTab === 0 && (
            <div>
              <FormSettings
                storeSlug={storeSlug}
                storeSettings={storeSettings}
                isStoreSettingsLoading={isStoreSettingsLoading}
                storeDetails={storeDetails}
              />
            </div>
          )}
          {activeTab === 1 && (
            <div>
              <ContactDetails
                storeSlug={storeSlug}
                storeDetails={storeDetails}
              />
            </div>
          )}
          {activeTab === 2 && (
            <div>
              <GroupCommunication
                storeSlug={storeSlug}
                storeDetails={storeDetails}
                storeSettings={storeSettings}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StoreSettings;
